/**
 * Query parameter names that must never reach an analytics event or an error
 * report, whichever app puts them in the address bar.
 *
 * The first five are what Stripe appends to a `return_url` after a redirect
 * payment or setup. The last three are ours, and each one is a live credential
 * or personal data sitting in a URL that every browser event carries:
 * `client_secret` authorises an embedded Connect session, `token_hash` lets its
 * holder set an account's password, and `email` names the person.
 *
 * The shared home for both platforms: the app strips them from `$screen`, the
 * web strips them from every PostHog property and redacts them in Sentry.
 */
export const STRIPE_RETURN_PARAMS = [
  "payment_intent",
  "payment_intent_client_secret",
  "setup_intent",
  "setup_intent_client_secret",
  "redirect_status",
  // Not a Stripe return param: the Connect pages put the AccountSession secret
  // in their own query, and no scrub caught it (EAH-2524 review).
  "client_secret",
  // Supabase email OTP, on the reset and verification screens of every app.
  "token_hash",
  "email",
] as const;

const STRIPE_RETURN_PARAM_SET: ReadonlySet<string> = new Set(
  STRIPE_RETURN_PARAMS,
);

/**
 * The same params with every name above removed. A new object, never a
 * mutation: callers hand in the router's own params object.
 */
export const omitStripeReturnParams = <T>(
  params: Readonly<Record<string, T>>,
): Record<string, T> => {
  const kept: Record<string, T> = {};
  for (const key of Object.keys(params)) {
    if (!STRIPE_RETURN_PARAM_SET.has(key)) kept[key] = params[key] as T;
  }
  return kept;
};

/** Where the first `?name=` or `&name=` of the list starts inside a value, or -1. */
const gluedNameIndex = (held: string): number => {
  let first = -1;
  for (const name of STRIPE_RETURN_PARAMS) {
    for (const lead of ["?", "&"]) {
      const at = held.indexOf(`${lead}${name}=`);
      if (at !== -1 && (first === -1 || at < first)) first = at;
    }
  }
  return first;
};

/** One query VALUE, cut where a listed name was glued into it. Untouched when none was. */
export const cutGluedReturnParams = (held: string): string => {
  const at = gluedNameIndex(held);
  return at === -1 ? held : held.slice(0, at);
};

/**
 * The same URL with every name above dropped from its query.
 *
 * For anything that carries a whole href rather than a parsed params object:
 * an analytics property, a breadcrumb, a referrer. Returns the input untouched
 * when it holds none of the names, when it is not a URL at all (PostHog writes
 * `$direct` into `$referrer`), or when parsing fails. Never throws, because
 * every caller is on a path that must not lose the event it is cleaning.
 */
export const stripStripeReturnParamsFromUrl = (value: string): string => {
  if (!STRIPE_RETURN_PARAMS.some((name) => value.includes(name))) return value;
  try {
    const url = new URL(value);
    let dropped = false;
    for (const name of STRIPE_RETURN_PARAMS) {
      if (!url.searchParams.has(name)) continue;
      url.searchParams.delete(name);
      dropped = true;
    }
    // A name glued inside another value (an email template adding `?token_hash=` after
    // our own `?next=`) is no param of its own, so the value is cut where it starts.
    for (const [key, held] of [...url.searchParams]) {
      const cut = cutGluedReturnParams(held);
      if (cut === held) continue;
      url.searchParams.set(key, cut);
      dropped = true;
    }
    return dropped ? url.toString() : value;
  } catch {
    return value;
  }
};
